import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import { api, EMAIL_RE, errMsg, fieldErrs } from "@/lib/api";
import { PageLoader } from "@/lib/hooks";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { AdminLayout, btnPrimary, FieldError, inputCls, Panel } from "@/components/admin/AdminLayout";
import { LogoManager } from "@/components/admin/LogoManager";
import { ContactsEditor, HoursEditor } from "@/components/admin/BusinessEditors";

const REQUIRED = "This field is required";

const validate = (f) => {
  const e = {};
  if (!f.name.trim()) e.name = REQUIRED;
  if (!f.contacts.some((c) => c.phone.trim())) e.contacts = "Add at least one phone number";
  if (f.public_email.trim() && !EMAIL_RE.test(f.public_email.trim())) e.public_email = "Enter a valid email";
  return e;
};

const Field = ({ id, label, hint, error, children }) => (
  <div>
    <Label htmlFor={id} className="text-sm text-zinc-200">{label}</Label>
    {hint && <p className="text-xs text-zinc-500">{hint}</p>}
    <div className="mt-1.5">{children}</div>
    <FieldError msg={error} testId={`${id}-error`} />
  </div>
);

export default function BusinessInfo() {
  const [form, setForm] = useState(null);
  const [logo, setLogo] = useState(null);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get("/admin/settings").then(({ data }) => {
      if (!alive) return;
      setLogo((cur) => cur ?? data.logo);
      setForm((cur) => cur ?? data);
    });
    return () => {
      alive = false;
    };
  }, []);

  if (!form) return <PageLoader testId="business-loading" />;
  const set = (k) => (e) => {
    setSaved(false);
    setForm({ ...form, [k]: e?.target ? e.target.value : e });
  };
  const text = (id, label, extra = {}) => (
    <Field id={`business-${id}`} label={label} error={errors[id]} hint={extra.hint}>
      {extra.area ? (
        <Textarea id={`business-${id}`} value={form[id]} onChange={set(id)} rows={4} className="bg-[#121214] border-zinc-700 text-base" data-testid={`business-${id}-input`} />
      ) : (
        <Input id={`business-${id}`} value={form[id]} onChange={set(id)} className={inputCls} type={extra.type || "text"} data-testid={`business-${id}-input`} />
      )}
    </Field>
  );

  const save = async (e) => {
    e.preventDefault();
    const errs = validate(form);
    setErrors(errs);
    setSaved(false);
    if (Object.keys(errs).length) return toast.error("Please fix the highlighted fields");
    setBusy(true);
    try {
      const { data } = await api.put("/admin/settings", form);
      setForm(data);
      setSaved(true);
      toast.success("Saved");
    } catch (err) {
      setErrors(fieldErrs(err));
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AdminLayout title="Business Info" testId="admin-business-page">
      <LogoManager logo={logo} onChange={setLogo} />
      <form onSubmit={save} noValidate data-testid="business-form">
        <Panel title="Shop details">
          <div className="space-y-4">
            {text("name", "Shop name")}
            {text("tagline", "Tagline")}
            {text("intro", "Intro text", { area: true })}
          </div>
        </Panel>
        <Panel title="Phone numbers">
          <ContactsEditor contacts={form.contacts} onChange={set("contacts")} error={errors.contacts} />
        </Panel>
        <Panel title="Location & email">
          <div className="space-y-4">
            {text("location", "Town / area", { hint: "e.g. Falcon, CO 80831" })}
            {text("street_address", "Street address (optional)", { hint: "Leave empty to show just the town" })}
            {text("location_note", "Location note", { hint: "e.g. Only 7 minutes from Falcon Walmart" })}
            {text("public_email", "Public email (optional)", { type: "email" })}
          </div>
        </Panel>
        <Panel title="Hours">
          <HoursEditor mode={form.hours_mode} hours={form.hours} onMode={set("hours_mode")} onHours={set("hours")} />
        </Panel>
        <div className="sticky bottom-0 -mx-4 flex items-center gap-4 border-t border-zinc-800 bg-[#121214]/95 px-4 py-3 backdrop-blur">
          <Button type="submit" disabled={busy || !form} className={`${btnPrimary} flex-1 sm:flex-none`} data-testid="business-save-button">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Save changes
          </Button>
          {saved && <span className="inline-flex items-center gap-1.5 text-[#7ee08a]" data-testid="business-saved-message"><CheckCircle2 className="h-5 w-5" /> Saved</span>}
        </div>
      </form>
    </AdminLayout>
  );
}
